// src/ResetPasswordPage.js
// MyTool NOW — Reset Password Page
// Reads ?token= from URL, submits new password to /api/auth/reset-password

import { useState, useEffect } from 'react';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5002';

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#0a0a0a',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'Segoe UI', sans-serif",
    padding: '20px',
  },
  card: {
    backgroundColor: '#1a1a1a',
    border: '1px solid #333',
    borderRadius: '12px',
    padding: '40px',
    width: '100%',
    maxWidth: '420px',
  },
  logo: {
    textAlign: 'center',
    marginBottom: '32px',
  },
  logoText: {
    fontSize: '24px',
    fontWeight: '800',
    color: '#FFD700',
    letterSpacing: '1px',
  },
  logoSub: {
    fontSize: '12px',
    color: '#888',
    marginTop: '4px',
    letterSpacing: '2px',
    textTransform: 'uppercase',
  },
  title: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#fff',
    marginBottom: '24px',
    textAlign: 'center',
  },
  label: {
    display: 'block',
    fontSize: '12px',
    fontWeight: '600',
    color: '#aaa',
    marginBottom: '6px',
    letterSpacing: '1px',
    textTransform: 'uppercase',
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    backgroundColor: '#111',
    border: '1px solid #444',
    borderRadius: '8px',
    color: '#fff',
    fontSize: '15px',
    marginBottom: '16px',
    boxSizing: 'border-box',
    outline: 'none',
  },
  button: {
    width: '100%',
    padding: '14px',
    backgroundColor: 'black',
    color: '#FFA500',
    border: '4px solid #E8761D',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '700',
    cursor: 'pointer',
    letterSpacing: '1px',
    marginTop: '8px',
  },
  buttonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  error: {
    backgroundColor: '#3a0000',
    border: '1px solid #ff4444',
    borderRadius: '8px',
    padding: '12px 14px',
    color: '#ff6666',
    fontSize: '14px',
    marginBottom: '16px',
    textAlign: 'center',
  },
  success: {
    backgroundColor: '#003a00',
    border: '1px solid #44ff44',
    borderRadius: '8px',
    padding: '16px',
    color: '#66ff66',
    fontSize: '14px',
    marginBottom: '16px',
    textAlign: 'center',
    lineHeight: '1.6',
  },
  warning: {
    backgroundColor: '#3a2000',
    border: '1px solid #ffaa00',
    borderRadius: '8px',
    padding: '16px',
    color: '#ffcc44',
    fontSize: '14px',
    textAlign: 'center',
    lineHeight: '1.6',
  },
  hint: {
    fontSize: '12px',
    color: '#666',
    marginTop: '-10px',
    marginBottom: '16px',
  },
  loginLink: {
    display: 'block',
    textAlign: 'center',
    marginTop: '20px',
    color: '#FFD700',
    fontSize: '13px',
    cursor: 'pointer',
    textDecoration: 'none',
    background: 'none',
    border: 'none',
    letterSpacing: '0.5px',
  },
};

export default function ResetPasswordPage() {
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    // Extract token from URL query string
    const params = new URLSearchParams(window.location.search);
    const t = params.get('token') || '';
    if (!t) {
      setError('No reset token found. Please request a new password reset link.');
    }
    setToken(t);
  }, []);

  const handleSubmit = async () => {
    setError('');

    if (!token) {
      setError('Invalid reset link. Please request a new one.');
      return;
    }
    if (!password || !confirm) {
      setError('Please fill in both password fields.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Password reset failed. Please try again.');
        return;
      }

      setSuccess(true);
    } catch (e) {
      setError('Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSubmit();
  };

  return (
    <div style={styles.page}>
      <style>{`
        .jfn-reset-btn:hover { background-color: #FFC107 !important; color: #000 !important; }
      `}</style>
      <div style={styles.card}>
        <div style={styles.logo}>
          <img
            src="/mtnlogo.png"
            alt="MyTool NOW"
            style={{ width: '220px', height: 'auto', display: 'block', margin: '0 auto', filter: 'drop-shadow(0 0 14px rgba(232,118,29,0.6))' }}
          />
          <div style={styles.logoSub}>SERVICE BUSINESS MANAGEMENT SOFTWARE</div>
        </div>

        <div style={styles.title}>Set New Password</div>

        {success ? (
          <>
            <div style={styles.success}>
              ✅ Password updated successfully!<br /><br />
              You can now sign in with your new password.
            </div>
            <button
              style={styles.button}
              onClick={() => window.location.href = '/login'}
            >
              GO TO SIGN IN
            </button>
          </>
        ) : !token ? (
          <div style={styles.warning}>
            ⚠️ Invalid or missing reset token.<br /><br />
            Please request a new password reset link.
            <br /><br />
            <button
              style={{ ...styles.button, marginTop: '0' }}
              onClick={() => window.location.href = '/forgot-password'}
            >
              REQUEST NEW LINK
            </button>
          </div>
        ) : (
          <>
            {error && <div style={styles.error}>{error}</div>}

            <label style={styles.label}>New Password</label>
            <div style={{ position: 'relative', marginBottom: '4px' }}>
              <input
                style={{ ...styles.input, marginBottom: 0, paddingRight: '44px' }}
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="••••••••"
                autoComplete="new-password"
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(v => !v)}
                style={{
                  position: 'absolute', right: '12px', top: '50%',
                  transform: 'translateY(-50%)', background: 'none',
                  border: 'none', cursor: 'pointer', color: '#888',
                  fontSize: '18px', padding: '0', lineHeight: 1
                }}
                tabIndex={-1}
              >{showPassword ? '🙈' : '👁️'}</button>
            </div>
            <div style={styles.hint}>Minimum 8 characters</div>

            <label style={styles.label}>Confirm Password</label>
            <div style={{ position: 'relative', marginBottom: '16px' }}>
              <input
                style={{ ...styles.input, marginBottom: 0, paddingRight: '44px' }}
                type={showConfirm ? 'text' : 'password'}
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="••••••••"
                autoComplete="new-password"
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowConfirm(v => !v)}
                style={{
                  position: 'absolute', right: '12px', top: '50%',
                  transform: 'translateY(-50%)', background: 'none',
                  border: 'none', cursor: 'pointer', color: '#888',
                  fontSize: '18px', padding: '0', lineHeight: 1
                }}
                tabIndex={-1}
              >{showConfirm ? '🙈' : '👁️'}</button>
            </div>

            <button
              className="jfn-reset-btn"
              style={{ ...styles.button, ...(loading ? styles.buttonDisabled : {}) }}
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? '⏳ UPDATING...' : 'SET NEW PASSWORD'}
            </button>

            <button
              style={styles.loginLink}
              onClick={() => window.location.href = '/login'}
            >
              ← Back to Sign In
            </button>
          </>
        )}
      </div>
    </div>
  );
}